import { useIntl } from '@capsicum/common';
import { ModalService } from '@capsicum/common-web';
import {
	useWalletWithdrawCurrencyActions,
	useWithdrawDataAtom,
	type WalletWithdrawFanTokens,
	type WithdrawDetailsChilizInfo,
	type WithdrawDetailsFanTokensInfo
} from '@capsicum/features/wallet/shared';

import {
	CHZOnHoldDrawer,
	FanTokenUnavailableMarketDrawer,
	NotEnoughCHZDrawer,
	NotEnoughFanTokensDrawer,
	WarningCHZWithdrawDrawer,
	WarningFanTokenWithdrawDrawer
} from '../../ui';
import {
	type FanTokenSelectorItem,
	FanTokenSelectorModal
} from '../../ui/fan-token-selector-modal/FanTokenSelectorModal';

export function useWithdrawPageActions({
	chilizInfo,
	fanTokensInfo,
	withdrawFanTokens
}: {
	chilizInfo: WithdrawDetailsChilizInfo;
	fanTokensInfo: WithdrawDetailsFanTokensInfo;
	withdrawFanTokens: Array<WalletWithdrawFanTokens> | undefined;
}) {
	const { $t } = useIntl();
	const { setFanToken } = useWithdrawDataAtom();

	const openCHZOnHoldDrawer = () => {
		ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<CHZOnHoldDrawer
					closeModal={close}
					minAmount={chilizInfo.minAmount}
					testID="withdraw-CHZOnHold-drawer"
				/>
			)
		});
	};

	const openFanTokenSelector = () => {
		ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<FanTokenSelectorModal
					data={withdrawFanTokens as Array<FanTokenSelectorItem>}
					texts={{
						button: $t('wallet.fantokenSelectModal.button'),
						searchInputPlaceholder: $t('wallet.fantokenSelectModal.searchInputPlaceholder'),
						title: $t('wallet.fantokenSelectModal.title'),
						warning: $t('wallet.fantokenSelectModal.warning', { minFanTokens: fanTokensInfo.minAmount })
					}}
					onClick={(chosenItem: FanTokenSelectorItem) => {
						if (chosenItem?.isCC2Enabled) {
							openWarningFanTokenWithdrawDrawer();
							setFanToken(chosenItem);
						} else {
							openFanTokenUnavailableMarketDrawer();
						}
						close();
					}}
				/>
			)
		});
	};

	const openFanTokenUnavailableMarketDrawer = () => {
		ModalService.addModal({
			detached: false,
			renderComponent: ({ close: closeChild }) => (
				<FanTokenUnavailableMarketDrawer
					closeModal={closeChild}
					testID="withdraw-tokenMarketUnavailable-drawer"
				/>
			)
		});
	};

	const openNotEnoughCHZDrawer = () => {
		ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<NotEnoughCHZDrawer
					closeModal={close}
					minAmount={chilizInfo.minAmount}
					testID="withdraw-notEnoughCHZ-drawer"
				/>
			)
		});
	};

	const openNotEnoughFanTokensDrawer = () => {
		ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<NotEnoughFanTokensDrawer
					closeModal={close}
					minAmount={fanTokensInfo.minAmount}
					testID="withdraw-notEnoughFanTokens-drawer"
				/>
			)
		});
	};

	const openWarningCHZWithdrawDrawer = () => {
		ModalService.addModal({
			detached: false,
			isClosable: false,
			renderComponent: ({ close }) => (
				<WarningCHZWithdrawDrawer close={close} testID="withdraw-warningCHZ-drawer" />
			)
		});
	};

	const openWarningFanTokenWithdrawDrawer = () => {
		ModalService.addModal({
			detached: false,
			isClosable: false,
			renderComponent: ({ close }) => (
				<WarningFanTokenWithdrawDrawer close={close} testID="withdraw-warningFT-drawer" />
			)
		});
	};

	return useWalletWithdrawCurrencyActions({
		chilizInfo,
		fanTokensInfo,
		openCHZOnHoldDrawer,
		openFanTokenSelector,
		openNotEnoughCHZDrawer,
		openNotEnoughFanTokensDrawer,
		openWarningCHZWithdrawDrawer
	});
}
